import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, InputAdornment } from '@mui/material';
import { Backdrop, CircularProgress } from '@mui/material';
import { formatApiMoneyForInput, parseColombianMoneyInput } from '../../../utils/money';
import { sessionRequestConfig } from '../../../utils/csrf';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

const API_URL = process.env.REACT_APP_API_URL;

async function actualizarReporteDiario(fecha, valorNuevo, tipoNuevo, tipoAnterior, valorAnterior) {

  try { const response = await axios.get(`${API_URL}/reporte_diario_cajas/`); 
    const reportes = response.data;
    const getReporte = (fecha, tipo) =>
      reportes.find(r => r.fecha === fecha && r.tipo === tipo.toLowerCase());
    
    // Si el tipo cambió
    if (tipoNuevo !== tipoAnterior) {
      // 1️⃣ Restar del reporte anterior
      let reporteAnterior = getReporte(fecha, tipoAnterior);
      if (reporteAnterior) {
        const nuevoValor = parseFloat(reporteAnterior.valor) - parseFloat(valorAnterior);
        await axios.put(`${API_URL}/reporte_diario_cajas/${reporteAnterior.id}/`, {
          valor: nuevoValor,
          tipo: tipoAnterior.toLowerCase(),
        });
      }
    
      // 2️⃣ Sumar al nuevo tipo (crear si no existe)
      let reporteNuevo = getReporte(fecha, tipoNuevo);
      if (reporteNuevo) {
        const nuevoValor = parseFloat(reporteNuevo.valor) + parseFloat(valorNuevo);
        await axios.put(`${API_URL}/reporte_diario_cajas/${reporteNuevo.id}/`, {
          valor: nuevoValor,
          tipo: tipoNuevo.toLowerCase(),
        });
      } else {
        await axios.post(`${API_URL}/reporte_diario_cajas/`, {
          fecha,
          tipo: tipoNuevo.toLowerCase(),
          valor: parseFloat(valorNuevo),
        });
      }
    
    } else {
      // 💡 El tipo no cambió, solo modificar valor según diferencia
      const reporte = getReporte(fecha, tipoNuevo);
      if (reporte) {
        const diferencia = parseFloat(valorNuevo) - parseFloat(valorAnterior);
        const nuevoValor = parseFloat(reporte.valor) + diferencia;
        await axios.put(`${API_URL}/reporte_diario_cajas/${reporte.id}/`, {
          valor: nuevoValor,
          tipo: tipoNuevo.toLowerCase(),
        });
      }
    }
  } catch (error) { console.error("Error al actualizar reporte diario:", error); }
}

function DialogModifyMovimiento({ dialogOpen, handleDialogClose, data, currentCaja, onMovementSuccess }) {
  const [tipo, setTipo] = useState('');
  const [monto, setMonto] = useState('');
  const [observacion, setObservacion] = useState('');
  const [error, setError] = useState({ tipo: '', monto: '', observacion: '' });
  const [loading, setLoading] = useState(false);
  const submitRef = useRef(false);

  useEffect(() => {
   if (data) {
     setTipo(data.tipo || "");
     setMonto(data.monto == null || data.monto === "" ? "" : formatApiMoneyForInput(data.monto));
     setObservacion(data.observacion || "");
   }
 }, [data]);

const handleGuardar = async () => {
  if (submitRef.current || loading) return;

  let errores = {};
  if (!tipo) errores.tipo = 'Debe seleccionar el tipo de movimiento.';
  if (!monto) errores.monto = 'Debe ingresar el monto.';
  if (!observacion) errores.observacion = 'Debe ingresar una observación.';

  if (Object.keys(errores).length > 0) {
    setError(errores);
    return;
  }

  submitRef.current = true;
  setLoading(true);

  try {
    const canonicalAmount = parseColombianMoneyInput(monto);
    const response = await axios.put(
      `${API_URL}/movimiento/${data.id}/`,
      {
        caja_id: currentCaja,
        movimiento: {
          isME: data.isME,
          tipo,
          monto: canonicalAmount,
          observacion,
        },
      },
      sessionRequestConfig()
    );
    const movimientoActualizado = response.data;

    await actualizarReporteDiario( movimientoActualizado.fecha_creacion.split(" ")[0], Number(movimientoActualizado.monto), movimientoActualizado.tipo, data.tipo, Number(data.monto) )    

    await onMovementSuccess?.();
    handleClose();
  } catch (error) {
    console.error(error);
    setError({
      general: getApiErrorMessage(
        error,
        'No fue posible guardar el movimiento.'
      ),
    });
  } finally {
    submitRef.current = false;
    setLoading(false);
  }
};

  const handleClose = () => {
    setError({ tipo: '', monto: '', observacion: '' });
    handleDialogClose();
  };

  return (
    <Dialog open={dialogOpen} onClose={handleClose}>
      <Backdrop
        open={loading}
        style={{ zIndex: 9999, color: '#fff' }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogTitle>Modificar Movimiento</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal" error={!!error.tipo}>
          <InputLabel>Tipo</InputLabel>
          <Select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            fullWidth
          >
            <MenuItem value="Ingreso">Ingreso</MenuItem>
            <MenuItem value="Egreso">Egreso</MenuItem>
          </Select>
          {error.tipo && <Typography color="error">{error.tipo}</Typography>}
        </FormControl>
        <TextField
          margin="normal"
          label="Monto"
          type="text"
          inputMode="decimal"
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, maxLength: 10 }} 
          fullWidth
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          error={!!error.monto}
          helperText={error.monto}
        />
        <TextField
          margin="normal"
          label="Observación"
          multiline
          fullWidth
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
          error={!!error.observacion}
          helperText={error.observacion}
        />
        {error.general && <Typography color="error">{error.general}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} color="error">
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleGuardar} color="primary" disabled={loading}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DialogModifyMovimiento;
